import React, { useState, useRef, useEffect, useCallback } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import HeroAiSearch from '../../../components/AiAssistant/HeroAiSearch';
import Icon from './Icon';
import { categoryHref } from './loadHomeV2Data';
import { CLIENT_APP_STORE, CLIENT_PLAY_STORE, HERO_SLIDES, HERO_INTERVAL_MS } from './content';
import s from './HomeV2.css';

// Redesigned homepage (home-v2), a port of the <main> sections of
// docs/proposals/besthand-home.html. Rendered only behind the dev-only newHomepageEnabled
// switch (see routes/home/index.js); the site-wide Header/Footer stay as they are.

const IMG = '/images/home';

// Category links carry encode(id), which uses a random salt: the server and client renders
// produce different (equally valid) URLs. React keeps the server attribute on hydration, so the
// mismatch is harmless; suppressHydrationWarning silences the dev-mode warning on these links.
function CategoryLink({ categoryId, className, children }) {
  return <a className={className} href={categoryHref(categoryId)} suppressHydrationWarning>{children}</a>;
}
const SEARCH_PROMPTS = ['What do you need help with?'];

// <picture> with a WebP source and a JPG fallback. `eager` only for above-the-fold images.
function Photo({ name, width, height, alt, eager = false, className }) {
  return (
    <picture>
      <source srcSet={`${IMG}/${name}.webp`} type="image/webp" />
      <img
        className={className}
        src={`${IMG}/${name}.jpg`}
        width={width}
        height={height}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        fetchpriority={eager ? 'high' : undefined}
        decoding="async"
      />
    </picture>
  );
}

function CategoryTabs({ tabs }) {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  if (!tabs.length) return null;
  const current = tabs[Math.min(active, tabs.length - 1)];

  // WAI-ARIA tabs: arrow keys move between tabs, Home/End jump to the ends.
  const onKeyDown = (e) => {
    const last = tabs.length - 1;
    let next = null;
    if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    if (tabRefs.current[next]) tabRefs.current[next].focus();
  };

  return (
    <>
      <div className={s.tabs} role="tablist" aria-label="Service categories">
        {tabs.map((t, i) => (
          <button
            key={t.key}
            ref={(el) => { tabRefs.current[i] = el; }}
            type="button"
            className={s.tab}
            role="tab"
            id={`home-tab-${t.key}`}
            aria-selected={i === active ? 'true' : 'false'}
            aria-controls="home-tab-panel"
            tabIndex={i === active ? 0 : -1}
            onClick={() => setActive(i)}
            onKeyDown={onKeyDown}
          >
            <Icon name={t.icon} className={s.tabIcon} />
            {t.label}
          </button>
        ))}
      </div>
      <div className={s.panel} role="tabpanel" id="home-tab-panel" aria-labelledby={`home-tab-${current.key}`}>
        <div className={s.panelTasks}>
          {current.tasks.map((task) => (
            <CategoryLink key={task.key} className={s.task} categoryId={task.categoryId}>{task.label}</CategoryLink>
          ))}
        </div>
        <p className={s.panelFoot}><a href={current.seeAllHref}>{current.seeAllLabel}</a></p>
      </div>
    </>
  );
}

// Rotating hero photos — ported from the old homepage's HeroBanner so the behaviour is the one
// Paul already approved: crossfade every HERO_INTERVAL_MS, pause while the tab is hidden, a manual
// dot tap restarts the clock, and prefers-reduced-motion users get one static frame.
function useHeroRotation(count) {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const restart = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (prefersReduced || count <= 1) return;
    timer.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setIdx((i) => (i + 1) % count);
    }, HERO_INTERVAL_MS);
  }, [prefersReduced, count]);
  useEffect(() => {
    restart();
    const onVis = () => { if (typeof document !== 'undefined' && !document.hidden) restart(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer.current) clearInterval(timer.current);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [restart]);
  const goTo = (i) => { setIdx(i); restart(); };
  return { idx, goTo };
}

function HeroPhotos({ slides }) {
  const { idx, goTo } = useHeroRotation(slides.length);
  return (
    <>
      {slides.map((sl, i) => (
        <div key={sl.image} className={`${s.heroSlide} ${i === idx ? s.heroSlideOn : ''}`} aria-hidden={i !== idx}>
          <Photo name={sl.image} width={sl.width} height={sl.height} alt={sl.alt} eager={i === 0} className={s.heroImg} />
        </div>
      ))}
      {slides.length > 1 && (
        <div className={s.heroDots} role="tablist" aria-label="Featured photos">
          {slides.map((sl, i) => (
            <button
              key={sl.image}
              type="button"
              role="tab"
              aria-selected={i === idx}
              aria-label={`Photo ${i + 1} of ${slides.length}`}
              className={`${s.heroDot} ${i === idx ? s.heroDotOn : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function HomeV2({ tabs = [], chips = [], projects = [], largeProjectsEnabled = false }) {
  return (
    <div className={s.homeV2}>
      {/* 1. Hero: one message, one action. */}
      <section className={s.hero}>
        <div className={`${s.wrap} ${s.heroWrap}`}>
          <div>
            <h1 className={s.heroTitle}>Home repairs, in expert hands.</h1>
            {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
            <p className={s.lead}>Book a background-checked local pro for plumbing, electrical, mounting, assembly and more. See the price before you book.</p>
            {/* AI search (aiJobAssistant) with the keyword search as fallback (and as the no-JS path). */}
            <div className={s.search}>
              <Icon name="search" className={s.searchIcon} />
              <HeroAiSearch prompts={SEARCH_PROMPTS} animate={false} buttonLabel="Get help" />
            </div>
            {chips.length > 0 && (
              <div className={s.chips} aria-label="Popular tasks">
                {chips.map((c) => (
                  <CategoryLink key={c.label} className={s.chip} categoryId={c.categoryId}>{c.label}</CategoryLink>
                ))}
              </div>
            )}
          </div>
          <div className={s.heroMedia}>
            <HeroPhotos slides={largeProjectsEnabled ? HERO_SLIDES : HERO_SLIDES.filter((sl) => sl.theme === 'quick')} />
            <div className={s.heroCard}>
              <Icon name="shield" className={s.heroCardIcon} />
              <div>
                {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
                <strong>Every pro is background-checked</strong>
                <span>Identity verified before their first job</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust strip: four facts, stated once. No dollar amounts (see Trust & Safety). */}
      <div className={s.trust}>
        <div className={`${s.wrap} ${s.trustWrap}`}>
          <div className={s.trustItem}><Icon name="shield" className={s.icon} />Background-checked pros</div>
          <div className={s.trustItem}><Icon name="tag" className={s.icon} />Upfront pricing</div>
          {/* LEGAL_REVIEW_REQUIRED: damage-protection wording; details live on /trust-safety. */}
          <a className={s.trustItem} href="/trust-safety"><Icon name="heart" className={s.icon} />Damage protection on every small job</a>
          <div className={s.trustItem}><Icon name="lock" className={s.icon} />Secure payment by Stripe</div>
        </div>
      </div>

      {/* 3. Browse by category (tabs), live categories and sub-categories. */}
      {tabs.length > 0 && (
        <section className={s.block} id="services">
          <div className={s.wrap}>
            <div className={s.head}>
              <h2>What can we help with?</h2>
              <p className={s.sub}>Pick a category to see popular jobs. Tap one to start booking.</p>
            </div>
            <CategoryTabs tabs={tabs} />
          </div>
        </section>
      )}

      {/* 4. How it works. */}
      <section className={`${s.block} ${s.soft}`} id="how">
        <div className={s.wrap}>
          <div className={s.head}>
            <h2>Book in three steps</h2>
            <p className={s.sub}>No phone calls, no waiting for quotes.</p>
          </div>
          <ol className={s.steps}>
            <li className={s.step}>
              <span className={s.stepNum} aria-hidden="true">1</span>
              <h3>Tell us what you need</h3>
              <p>Describe the job in a few words. Your address is filled in for you.</p>
            </li>
            <li className={s.step}>
              <span className={s.stepNum} aria-hidden="true">2</span>
              <h3>Pick a time and see the price</h3>
              <p>Choose when it suits you. The price is shown before you commit.</p>
            </li>
            <li className={s.step}>
              <span className={s.stepNum} aria-hidden="true">3</span>
              <h3>Confirm and relax</h3>
              {/* LEGAL_REVIEW_REQUIRED: payment-timing wording (hold at booking, capture after the job). */}
              <p>Your pro arrives at the agreed time. We place a hold on your card when you book, and you&apos;re only charged once the job is done.</p>
            </li>
          </ol>
          <div className={s.howCta}><a className={`${s.btn} ${s.btnPrimary}`} href="/category?type=all">Book a pro</a></div>
        </div>
      </section>

      {/* 5. Popular projects. */}
      {projects.length > 0 && (
        <section className={s.block}>
          <div className={s.wrap}>
            <div className={s.head}>
              <h2>Popular projects</h2>
              <p className={s.sub}>The jobs people book most.</p>
            </div>
            <div className={s.projects}>
              {projects.map((p) => (
                <CategoryLink key={p.title} className={s.project} categoryId={p.categoryId}>
                  <Photo name={p.image} width={p.width} height={p.height} alt={p.alt} className={s.projectImg} />
                  <div className={s.projectBody}><h3>{p.title}</h3><p>{p.text}</p></div>
                </CategoryLink>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. Large projects: only when the global Large Projects flag is ON. */}
      {largeProjectsEnabled && (
        <section className={`${s.block} ${s.flushTop}`}>
          <div className={s.wrap}>
            <div className={s.band}>
              <div className={s.bandText}>
                <p className={s.bandEyebrow}>Large projects</p>
                <h2>Renovating? Get quotes from verified companies.</h2>
                <p>Describe your kitchen, bathroom or porch project once. Licensed companies send you competing quotes.</p>
                <ul className={s.bandList}>
                  <li><Icon name="check" className={s.bandIcon} />No site visit needed to start</li>
                  <li><Icon name="check" className={s.bandIcon} />Licensed and insured companies</li>
                  <li><Icon name="check" className={s.bandIcon} />Pay by milestone, held safely until each step is done</li>
                </ul>
                <a className={`${s.btn} ${s.btnLight}`} href="/post-a-project">Get project quotes</a>
              </div>
              <div className={s.bandMedia}>
                <Photo name="large-projects-bathroom" width={1200} height={801} alt="A renovated bathroom with a marble vanity and a glass shower" className={s.bandImg} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 7. App download. */}
      <section className={`${s.block} ${s.soft}`}>
        <div className={`${s.wrap} ${s.app}`}>
          <div>
            <h2>Manage every job from your phone</h2>
            <p className={s.sub}>Book, chat with your pro and track arrival in the BestHand app.</p>
            <ul className={s.appPoints}>
              <li><Icon name="clock" className={s.appIcon} />See when your pro is on the way</li>
              <li><Icon name="chat" className={s.appIcon} />Message your pro directly</li>
              <li><Icon name="lock" className={s.appIcon} />Pay securely when the work is done</li>
            </ul>
            <div className={s.stores}>
              <a className={s.store} href={CLIENT_APP_STORE} target="_blank" rel="noopener noreferrer"><small>Download on the</small><b>App Store</b></a>
              <a className={s.store} href={CLIENT_PLAY_STORE} target="_blank" rel="noopener noreferrer"><small>Get it on</small><b>Google Play</b></a>
            </div>
          </div>
          <div className={s.phone} aria-hidden="true">
            <div className={s.phoneBar} />
            <div className={s.phoneCard}><b>TV mounting</b><span>Saturday, 10:00 AM</span></div>
            <div className={s.phoneCard}><b>Your pro is on the way</b><span>Arriving in about 15 min</span></div>
            <div className={s.phoneCard}><b>Price</b><span>Confirmed before you booked</span></div>
            <div className={s.phoneCta}>Message your pro</div>
          </div>
        </div>
      </section>

      {/* 8. FAQ (native <details>). */}
      <section className={s.block}>
        <div className={s.wrap}>
          <div className={s.head}><h2>Questions</h2></div>
          <div className={s.faq}>
            <details>
              <summary>How are BestHand pros checked?</summary>
              {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
              <p>Every pro completes identity verification and a background check before they can accept a job.</p>
            </details>
            <details>
              <summary>When do I pay?</summary>
              {/* LEGAL_REVIEW_REQUIRED: payment-timing wording (hold at booking, capture after the job). */}
              <p>We place a hold on your card when you book. You&apos;re only charged once the job is done, through secure payment by Stripe.</p>
            </details>
            <details>
              <summary>How much does it cost?</summary>
              <p>You see your full price, fees included, before you confirm.</p>
            </details>
            <details>
              <summary>What if something goes wrong?</summary>
              {/* LEGAL_REVIEW_REQUIRED: damage-protection wording; no amount stated here on purpose. */}
              <p>Small jobs include damage protection. See <a className={s.inlineLink} href="/trust-safety">Trust &amp; Safety</a> for the details.</p>
            </details>
            <details>
              <summary>Do you offer discounts for seniors and veterans?</summary>
              <p>Yes. Eligible seniors and veterans get special rates and priority support. <a className={s.inlineLink} href="/senior-veteran-discount">See how to qualify</a>.</p>
            </details>
          </div>
        </div>
      </section>

      {/* 9. Pro recruitment, one line. */}
      <section className={`${s.block} ${s.flushTop}`}>
        <div className={s.wrap}>
          <div className={s.proCta}>
            <div>
              <h2>Are you a pro?</h2>
              <p className={s.sub}>Find jobs near you, set your own schedule and keep your rate.</p>
            </div>
            <a className={`${s.btn} ${s.btnOutline}`} href="/become-a-pro">Become a pro</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default withStyles(s)(HomeV2);
